import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PWA_UPDATE_AVAILABLE_EVENT,
  triggerPwaReload,
} from '../../utils/pwaRegister'

/**
 * Shown when vite-plugin-pwa detects a new service worker (e.g. after deploy).
 * Critical for home-screen / standalone installs where a normal reload may not fetch new assets.
 */
function PwaUpdateBanner() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onAvail = () => setOpen(true)
    window.addEventListener(PWA_UPDATE_AVAILABLE_EVENT, onAvail)
    return () => window.removeEventListener(PWA_UPDATE_AVAILABLE_EVENT, onAvail)
  }, [])

  const onUpdate = useCallback(async () => {
    try {
      await triggerPwaReload()
    } catch {
      window.location.reload()
    }
  }, [])

  if (!open) return null

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] flex justify-center px-4 pointer-events-none"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      role="status"
    >
      <div className="pointer-events-auto mb-4 flex w-full max-w-lg flex-col gap-3 rounded-2xl border border-emerald-200 bg-white p-4 shadow-xl sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">
            {t('pwa.updateTitle', 'Update available')}
          </p>
          <p className="mt-1 text-sm text-gray-600 leading-snug">
            {t(
              'pwa.updateBody',
              'A new version of ReecoVerse is ready. Update for the latest fixes and features.',
            )}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            className="rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
            onClick={() => setOpen(false)}
          >
            {t('pwa.later', 'Later')}
          </button>
          <button
            type="button"
            className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700"
            onClick={onUpdate}
          >
            {t('pwa.updateNow', 'Update now')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PwaUpdateBanner
